/**
 * @param {Element} elToWrap
 * @param {string} wrapperHtmlString - an HTML string containing a node to wrap around given element
 */
export function wrapEl(elToWrap, wrapperHtmlString) {
  if (!elToWrap) {
    console.warn('Element to wrap is undefined');
    return;
  }
  elToWrap.insertAdjacentHTML('beforebegin', wrapperHtmlString);
  const wrapper = elToWrap.previousElementSibling;
  wrapper.appendChild(elToWrap);
}

/**
 * Take a string of HTML or text and unwrap any outer wrapping elements included in tags
 * @todo support stuff other than paragraph tags - could convert to node then tag innerHTML or text content
 * @param {String} text
 */
export function unwrap(text) {
  if (typeof text === 'string') {
    // use a regex literal instead of a string to avoid double escaping
    const regx = /^[<p>]+|[</p>]+$/g; // leading or trailing paragraphs
    return text.trim().replace(regx, '');
  }
  return text; // right now do nothing if not a string (i.e. if HTMLElement)
}

/**
 * Important to note that this function parses xml strings with the content type 'text/html'
 * Also, it returns an HTMLElement that wraps the content of the htmlString,
 * so to get result as string you call `wrapper.innerHTML`
 * and to get result as nodeList you would call `wrapper.childNodes`
 *
 * @param htmlString - text html content
 * @return {DocumentFragment} - a doc fragment containing all nodes in htmlstring
 */
export function parseNodesFromHtmlString(htmlString) {
  const parser = new DOMParser(),
        HTMLDocument = parser.parseFromString(htmlString, 'text/html'),
        documentFragment = document.createDocumentFragment();

  documentFragment.append(...HTMLDocument.body.childNodes);
  return documentFragment;
}

/**
 * DocumentFragment API does not include methods for serializing/deserializing nodes as text
 * Given a doc fragment this method gives us a string version of the html inside it.
 * @param {DocumentFragment} documentFragment
 * @return {*}
 */
export function documentFragmentToString(documentFragment) {
  const div = document.createElement('div');
  div.append(...documentFragment.childNodes);
  return div.innerHTML;
}

/**
 * This function takes a string that may contain HTML and returns the string with NO html tags
 * @param {string} html
 * @return {string}
 */
export function stripHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
}

/**
 * Scroll to the top (of the page or given element)
 * @param {Element|Window} target
 */
export function scrollTop(target = window) {
  target.scrollTo({top: 0, left: 0, behavior: 'smooth'});
}

/**
 * Focus the input element or contenteditable element
 * @param {HTMLElement|HTMLInputElement|HTMLTextAreaElement|String} input - could be an input or a contenteditable element
 * @param {{position: 'start' | 'end'}} options - cursor position defaults to end
 */
export function focusInput(input, {position} = {position: 'end'}) {
  let element = input;
  if (typeof input === 'string') {
    element = document.querySelector(input);
  }
  if (!element) return;
  element.focus();
  if (position === 'start') return;

  // Set cursor to the end otherwise
  // @see https://stackoverflow.com/a/3866442
  if (element.isContentEditable) {
    const range = document.createRange();// Create a range (a range is a like the selection but invisible)
    range.selectNodeContents(element);// Select the entire contents of the element with the range
    range.collapse(false);// collapse the range to the end point. false means collapse to end rather than the start

    const selection = window.getSelection();// get the selection object (allows you to change selection)
    selection.removeAllRanges();// remove any selections already made
    selection.addRange(range);// make the range you have just created the visible selection
  } else {
    // it's an input
    element.setSelectionRange(element.value.length, element.value.length);
  }
}

/**
 * Attempt to find the first textual input and focus it (if it is found). Should match text inputs of all kinds.
 * Froala is picked up with "textarea" but requires special treatment (see below) because of the fact it mutates the DOM at an unknown time (to this script)
 */
export const focusFirstTextInput = () => {
  const input = document.querySelector('textarea, input[type="email"], input[type="password"], input[type="text"]'),
        isFroalaInput = input && input.style.display === 'none';

  if (isFroalaInput) {
    const ob = new MutationObserver((mutationList) => {
      const froalaInput = mutationList[0].target;
      focusInput(froalaInput);
      ob.disconnect();
    });
    // assumes froala inserts HTML at same sibling level as the textarea input
    ob.observe(input.parentElement, {attributeFilter: ['data-loaded'], attributes: true, subtree: true});
  } else if (input) {
    input.focus();
  }
};

/**
 * Loadd a css variables value in javascript
 * @param {string} name - should be something like --bs-info
 * @returns {string}
 */
export function cssVar(name) {
  return window.getComputedStyle(document.documentElement).getPropertyValue(name);
}

/**
 * Extract value from various HTML input elements with robust handling
 * @param {HTMLElement} element - The DOM element to extract value from
 * @returns {string|boolean|Array|File|FileList|null} - The extracted value based on element type
 */
export function getElementValue(element) {
  if (!element) return null;

  const {type} = element,
        tag = element.tagName.toLowerCase();

  if (type === 'file') {
    // Return FileList or null if no files selected
    if (element.files.length === 0) {
      return null;
    }
    return element.multiple ? Array.from(element.files) : element.files[0];
  }

  if (type === 'radio') {
    // For radio buttons, find the checked one in the group by name
    const {name} = element;
    if (name) {
      const radios = document.getElementsByName(name),
            checked = Array.from(radios).find((el) => el.checked);
      return checked ? checked.value : null;
    }
    return element.checked ? element.value : null;
  }

  if (type === 'checkbox') {
    const {name} = element;
    if (name) {
      const checkboxes = document.getElementsByName(name);
      if (checkboxes.length === 1) {
        return element.checked;
      }
      return Array.from(checkboxes)
        .filter((el) => el.checked)
        .map((el) => el.value);
    }
    return element.checked;
  }

  if (tag === 'select') {
    if (element.multiple) {
      return Array.from(element.selectedOptions).map((opt) => opt.value);
    }
    return element.value;
  }

  return element.value || element.textContent || '';
}
